<template>
  <div
    class="test-method-matrix px-8 py-10 container"
    style="max-width: 1150px;margin:0 auto;"
  >
    <div class="flex flex-wrap">
      <!--
      <SideBar class="sm:w-1/5 sm:order-first sm:mt-0 w-full order-last mt-4" />
      -->
      <div class="w-full">
        <TitleComponent
          class="py-8"
          title="Situation-Specific Explosives Classification (SSEC)"
          size="large"
        />

        <div>
          <p class="font-bold">
            <a
              style="color:#ef9a3d;"
              target="_blank"
              href="https://firebasestorage.googleapis.com/v0/b/etusersgroup.appspot.com/o/ETUG-GS01-25%20Standard%20for%20Situation-Specific%20Explosives%20Classification%2C%20Rev%200.pdf?alt=media&token=4fe793d5-88e2-4e23-9c34-decdcb254d28"
            >
              ETUG-GS01-25: ETUG Standard for Situation-Specific Explosives
              Classification (SSEC)
            </a>
            was adopted by consensus at the October 2025 annual meeting and
            supersedes the legacy
            <a
              style="color:#ef9a3d;"
              target="_blank"
              href="https://firebasestorage.googleapis.com/v0/b/etusersgroup.appspot.com/o/ETUG-Standard-ETUG-GS01-15.pdf?alt=media&token=8f0c42a1-7359-4473-82c4-7997887eca5e"
            >
              ETUG-GS01-15: ETUG Standard for In-Process Hazard Classification
              of Explosives (2015)</a
            >, the initial standard developed by ETUG.
          </p>

          <p class="mt-4">
            “Situation-Specific Explosives Classification” (SSEC) was chosen as
            the new name for the standard since it more accurately reflects the
            unique and varied hazard types and severities associated with
            explosives in the diverse situations found in all explosive
            lifecycle stages. The SSEC standard leverages, when technically
            relevant, the transport classification for limited operations
            involving removal or partial removal of the explosive from the
            transport packaging.
          </p>

          <p class="mt-4">
            The objective for the original ETUG In-Process Standard and now the
            SSEC revision is to establish an explosive classification protocol
            for all lifecycle stages that builds on:
          </p>

          <!-- snug gap here -->
          <ul class="list-disc pl-6 space-y-2 mt-1">
            <li>
              Industry best practices for explosives characterization testing
              that account for varying explosive key parameters such as
              configuration, confinement, quantity, composition, etc.;
            </li>
            <li>
              U.S. Occupational Safety and Health Administration (OSHA) Process
              Safety Management (PSM) and Risk Management Program (RMP)
              principles; and
            </li>
            <li>
              When applicable, it harmonizes with the United Nations (UN)
              Recommendations on the Transport of Dangerous Goods, Manual of
              Tests and Criteria (MTC), UN Globally Harmonized System of
              Classification and Labelling of Chemicals (GHS), 9th Revision,
              Chapter 2.1 “Explosives”, and the U.S. Department of Defense (DoD)
              Joint Technical Bulletin (TB) 700-2, “Department of Defense
              Ammunition and Explosives Hazard Classification Procedures” for
              transport and storage;
            </li>
          </ul>

          <p class="mt-4">
            The explosive transport and extrapolated storage classification
            schemes for static situations do not address the varied and unique
            dynamic and semi-dynamic situations that exist in other lifecycle
            stages. It is essential that classification of explosives in these
            situations reflect the potential reaction type and hazard severity
            associated with applicable explosives key parameters, credible
            threats, and environments.
          </p>

          <p class="mt-4">
            This standard has been revised to:
          </p>

          <ul class="list-disc pl-6 space-y-2 mt-1">
            <li>
              Refine the name to “Situation-Specific Explosives Classification
              (SSEC)”.
            </li>
            <li>
              Leverage the transport classification as far as practical to
              address explosives removed from the transport packaging, based on
              the UN GHS.
            </li>
            <li>
              Improve classification resolution for In-Process 1.3 and 1.4
              explosives.
            </li>
            <li>
              Refine In-Process 1.4 classification protocol for qualifying
              substances.
            </li>
            <li>
              Refine In-Process 3 and 4.1 classification protocol for qualifying
              desensitized substances.
            </li>
            <li>Include the U.S. DoD unpackaged explosives section.</li>
          </ul>
          <div class="flex flex-wrap mt-12">
            <!-- Left column -->
            <div class="w-full md:w-1/2 px-4">
              <h2 class="text-xl font-bold mb-6">
                Resources for SSEC Method 1: Limited Operations - "Unpackaged"
              </h2>

              <div class="space-y-6">
                <router-link class="link block" to="/ssec-method-1-dod">
                  <div class="flex items-start gap-4">
                    <div
                      style="background-color:#ef9a3d;width:3rem;height:3rem;"
                      class="rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <img
                        class="icon-white"
                        width="22"
                        height="22"
                        src="@/assets/stream-solid.svg"
                      />
                    </div>
                    <span class="text-lg leading-snug">
                      DoD Unpackaged Explosives Articles in JHCS (in DoD
                      Lifecycle Stages 5, 6, and/or 7)
                    </span>
                  </div>
                </router-link>

                <router-link class="link block mt-4" to="/ssec-method-1-other">
                  <div class="flex items-start gap-4">
                    <div
                      style="background-color:#ef9a3d;width:3rem;height:3rem;"
                      class="rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <img
                        class="icon-white"
                        width="22"
                        height="22"
                        src="@/assets/stream-solid.svg"
                      />
                    </div>
                    <span class="text-lg leading-snug">
                      Other Unpackaged Explosives
                    </span>
                  </div>
                </router-link>
              </div>
            </div>

            <!-- Right column -->
            <div class="w-full md:w-1/2 px-4 mt-10 md:mt-0">
              <h2 class="text-xl font-bold mb-6">
                Resources for SSEC Method 2: In-Process
              </h2>
              <div>
                <!-- spacer to align with left column links -->
                <div style="height: 1.9rem;"></div>

                <router-link class="link block" to="/ssec-method-2">
                  <div class="flex items-start gap-4">
                    <div
                      style="background-color:#ef9a3d;width:3rem;height:3rem;"
                      class="rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <img
                        class="icon-white"
                        width="22"
                        height="22"
                        src="@/assets/stream-solid.svg"
                      />
                    </div>
                    <span class="text-lg leading-snug">
                      SSEC Method 2: In-Process
                    </span>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TitleComponent from "@/components/TitleComponent";
//import SideBar from "@/components/IPSeriesSideBar";

export default {
  components: {
    TitleComponent
    //SideBar
  }
};
</script>

<style lang="scss">
.test-method-matrix {
  .icon-white {
    filter: brightness(0) invert(1);
  }

  .link:hover {
    color: #ef9a3d;
  }
}
</style>
